import { twMerge } from 'tailwind-merge';
import { Controller, useFormContext } from 'react-hook-form';
import { transformValue, transformValueOnBlur, transformValueOnChange } from 'minimal-shared/utils';

// ----------------------------------------------------------------------

export type RHFTextFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  name: string;
  label?: string;
  helperText?: string;
  fullWidth?: boolean;
};

export function RHFTextField({
  name,
  label,
  helperText,
  className,
  type = 'text',
  fullWidth = true,
  ...other
}: RHFTextFieldProps) {
  const { control } = useFormContext();
  const isNumberType = type === 'number';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const hasValue = field.value !== undefined && field.value !== null && field.value !== '';

        return (
          <div className={twMerge(['flex flex-col', fullWidth && 'w-full'])}>
            <div className="relative">
              <input
                {...field}
                id={name}
                placeholder=" "
                value={isNumberType ? transformValue(field.value) : field.value}
                onChange={(event) => {
                  const value = isNumberType
                    ? transformValueOnChange(event.target.value)
                    : event.target.value;
                  field.onChange(value);
                }}
                onBlur={(event) => {
                  const value = isNumberType
                    ? transformValueOnBlur(event.target.value)
                    : event.target.value;
                  field.onChange(value);
                }}
                type={isNumberType ? 'text' : type}
                inputMode={isNumberType ? 'decimal' : undefined}
                pattern={isNumberType ? '[0-9]*\\.?[0-9]*' : undefined}
                autoComplete="new-password"
                className={twMerge([
                  'peer h-[40px] w-full px-3',
                  'flex items-center text-sm',
                  'rounded-md border outline-none transition-all',
                  'bg-white border-gray-300 focus:ring-2 focus:ring-[#1C252E] focus:border-[#1C252E]',
                  error && 'border-error focus:ring-error-dark focus:border-error-dark',
                  className,
                ])}
              />

              {label && (
                <label
                  htmlFor={name}
                  className={twMerge([
                    'absolute left-2 px-1 bg-white pointer-events-none transition-all',
                    'top-1/2 -translate-y-1/2 text-sm',
                    error ? 'text-error-dark' : 'text-gray-500',
                    'peer-focus:top-0 peer-focus:text-xs peer-focus:-translate-y-1/2',
                    !error && 'peer-focus:text-[#1C252E]',
                    hasValue && 'top-0 text-xs -translate-y-1/2',
                  ])}
                >
                  {label}
                </label>
              )}
            </div>

            <p
              className={twMerge([
                'mt-1 text-left text-xs md:text-sm',
                error ? 'text-error-dark' : 'text-gray-500',
              ])}
            >
              {error?.message || helperText}
            </p>
          </div>
        );
      }}
    />
  );
}
